import React, { Dispatch, SetStateAction } from 'react';
import { Autocomplete, Box, CircularProgress, InputLabel, TextField } from '@mui/material';
import { DebouncedFormTextField } from '../../Components/CustomUIElements/FormTextField';
import { SingleDateTimePicker } from '../../Components/CustomUIElements/SingleDatePicker';
import { sharedClasses } from '../../Components/CustomUIElements/sharedClasses';
import { classes } from './styles';
import AIStudioNewJob, {
  IGeneratedContent
} from '../../../AIStudio/NewJobIntegration/AIStudioNewJob';
import moment from 'moment';
import { createJobDefault } from '../config';

export default function NewJobStep2({
  newJobData,
  setNewJobsData,
  fieldRefs,
  showEntitySection,
  entities,
  loadingEntities,
  selectedEntity,
  setSelectedEntity,
  aiStudioEnabled,
  createType,
  generatedContent,
  setGeneratedContent,
  userTimezone,
  clonedJob
}: {
  newJobData: typeof createJobDefault;
  setNewJobsData: Dispatch<SetStateAction<typeof createJobDefault>>;
  fieldRefs: {
    reference: React.RefObject<HTMLInputElement>;
    title: React.RefObject<HTMLInputElement>;
    location: React.RefObject<HTMLInputElement>;
    expires_at: React.RefObject<HTMLInputElement>;
  };
  showEntitySection: boolean;
  entities: { id: number; name: string }[];
  loadingEntities: boolean;
  selectedEntity: { id: number; name: string } | null;
  setSelectedEntity: Dispatch<SetStateAction<{ id: number; name: string } | null>>;
  aiStudioEnabled: boolean;
  createType: string;
  generatedContent: IGeneratedContent;
  setGeneratedContent: Dispatch<SetStateAction<IGeneratedContent>>;
  userTimezone: string;
  clonedJob: string | null;
}) {
  return (
    <Box sx={classes.modalContent} id="new-job-modal-content" aria-label="New job modal content">
      <Box id="new-job-modal-title" sx={classes.modalHeaderContent}>
        Tell us the details of your job
      </Box>
      <Box role="form" id="new-job-form" aria-label="New job form">
        <Box sx={classes.modalFormLine}>
          <DebouncedFormTextField
            onDebouncedChange={(value: string | number) =>
              setNewJobsData((prev) => ({
                ...prev,
                reference: { ...prev.reference, value: String(value) }
              }))
            }
            id="reference"
            dataTestId="reference"
            label="Reference"
            defaultValue={newJobData?.reference?.value}
            required={newJobData?.reference?.required}
            error={newJobData?.reference?.error}
            innerRef={fieldRefs.reference}
            styles={{ width: '250px' }}
          />
          <DebouncedFormTextField
            onDebouncedChange={(value: string | number) =>
              setNewJobsData((prev) => ({
                ...prev,
                title: { ...prev.title, value: String(value) }
              }))
            }
            id="title"
            dataTestId="title"
            label="Title"
            defaultValue={newJobData?.title?.value}
            required={newJobData?.title?.required}
            error={newJobData?.title?.error}
            innerRef={fieldRefs.title}
            styles={{ width: '250px' }}
          />
        </Box>
        <Box sx={classes.modalFormLine}>
          <DebouncedFormTextField
            onDebouncedChange={(value: string | number) =>
              setNewJobsData((prev) => ({
                ...prev,
                location: { ...prev.location, value: String(value) }
              }))
            }
            id="location"
            dataTestId="location"
            label="City/Town"
            error={newJobData?.location?.error}
            defaultValue={newJobData?.location?.value}
            required={newJobData?.location?.required}
            innerRef={fieldRefs.location}
            styles={{ width: '250px' }}
          />
          {showEntitySection && !clonedJob && (
            <Autocomplete
              disablePortal
              autoHighlight
              options={entities}
              loading={loadingEntities}
              getOptionLabel={(option) => option.name}
              noOptionsText="No entities"
              value={entities.find((e) => e.id === selectedEntity?.id)}
              onChange={(_event, newValue) =>
                newValue ? setSelectedEntity(newValue) : setSelectedEntity(entities[0])
              }
              sx={{ ...sharedClasses.formAutocomplete, width: '250px', marginTop: '24px' }}
              slotProps={{ listbox: { style: sharedClasses.autoCompleteListStyles } }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  sx={{ input: { height: '20px' } }}
                  label="Entity"
                  slotProps={{
                    inputLabel: { shrink: true },
                    input: {
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {loadingEntities ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      )
                    }
                  }}
                />
              )}
            />
          )}
        </Box>
        <Box
          sx={{ ...classes.modalFormLine, columnGap: '25px', position: 'relative' }}
          ref={fieldRefs.expires_at}
        >
          <Box id="expiry-date" data-testid="expiry-date">
            <SingleDateTimePicker
              label={
                <InputLabel htmlFor="text-input" sx={sharedClasses.inputLabel}>
                  Expiry date and time<span>*</span>
                </InputLabel>
              }
              inputStyles={classes.datePicker}
              onChange={(value) => {
                setNewJobsData((prev: any) => ({
                  ...prev,
                  expires_at: {
                    ...prev.expires_at,
                    value: moment(value).toISOString(),
                    error: ''
                  }
                }));
              }}
              timezone={userTimezone ?? moment.tz.guess()}
              value={newJobData.expires_at.value}
              disablePast={true}
            />
          </Box>
          {newJobData.expires_at.error && (
            <Box sx={{ ...classes.errorBox, bottom: '-35px' }}>{newJobData.expires_at.error}</Box>
          )}
        </Box>
      </Box>
      {aiStudioEnabled && createType !== 'Clone existing job' && (
        <AIStudioNewJob
          generatedContent={generatedContent}
          setGeneratedContent={setGeneratedContent}
          prefillData={{
            title: newJobData?.title?.value,
            country: newJobData?.location?.value
          }}
        />
      )}
    </Box>
  );
}
